import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { InputDialogComponent, InputDialogData, InputDialogResult } from '../components/call-center-database/input-dialog';
import { CallCenterDatabaseTableData } from '../interfaces/call-center-database.interface';
import { AdminInteraction } from './interactions.service';
import { CallCenterTableUtils } from '../components/call-center-database/database-table/utils/call-center-table.utils';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class CallCenterDialogService {

  constructor(
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  /**
   * Opens status edit dialog for a company
   */
  openStatusDialog(
    element: CallCenterDatabaseTableData, 
    companyLogs: AdminInteraction[],
    statusOptions: any[],
    logsError?: string
  ): Observable<InputDialogResult | undefined> {
    // Parse contact information
    const officials = CallCenterTableUtils.parseOfficials(element.officials);
    const phoneNumbers = CallCenterTableUtils.parsePhoneNumbers(element.phoneNumbers);
    
    // Use emails array directly
    const emails: string[] = [];
    if (element.emails && Array.isArray(element.emails) && element.emails.length > 0) {
      emails.push(...element.emails.filter((e: string) => e && e.trim() !== 'Nincs email'));
    }
    
    // Use emailValidStatuses object from backend
    const emailValidStatuses: { [key: string]: boolean | null } = element.emailValidStatuses || {};
    
    // Open the input dialog for status editing
    const dialogData: InputDialogData = {
      title: 'Státusz módosítása',
      label: 'Megjegyzés',
      placeholder: 'Írja be a megjegyzést...',
      initialValue: '', // Üres input mező megnyitáskor
      required: false,
      inputType: 'text',
      subtitle: element.companyName,
      details: `${element.registrationNumber} | ${element.postalCode} ${element.county}`,
      officials: officials,
      phoneNumbers: phoneNumbers,
      emails: emails,
      // Status selector configuration
      showStatusSelector: true,
      statusOptions: statusOptions.filter(option => option.value !== 'all'),
      // Pass validstatus data for chip coloring
      officialValidStatuses: element.officialValidStatuses,
      phoneValidStatuses: element.phoneValidStatuses,
      emailValidStatuses: emailValidStatuses,
      // Pass company ID for backend updates
      companyId: element.id,
      // Add logs data
      showLogs: true,
      logs: companyLogs,
      logsError: logsError || null,
      // Current user for edit permissions
      currentUser: this.authService.getCurrentUser() ? {
        id: this.authService.getCurrentUser()!.id,
        jobs: this.authService.getCurrentUser()!.jobs
      } : undefined,
      // Official createdBy data
      officialCreatedBy: element.officialCreatedBy || {},
      // Phone/Email createdBy data
      phoneCreatedBy: element.phoneCreatedBy || {},
      emailCreatedBy: element.emailCreatedBy || {}
    };

    // Determine dialog width based on content
    // Check if there's substantial contact information that needs wide layout
    const hasContactInfo = 
      (officials && officials.length > 0) || 
      (phoneNumbers && phoneNumbers.length > 0) || 
      (emails && emails.length > 0);
    
    const hasLogs = companyLogs && companyLogs.length > 0;
    
    // Use wide layout (1200px) if there are logs OR substantial contact info
    // This prevents content squishing when logs fail to load but contact data exists
    const needsWideLayout = hasLogs || hasContactInfo;
    const dialogWidth = needsWideLayout ? '1200px' : '500px';
    
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: dialogWidth,
      maxWidth: '95vw', // Allow responsive on very small screens
      minWidth: needsWideLayout ? '1000px' : '400px',
      data: dialogData,
      disableClose: false,
      panelClass: [
        'input-dialog-overlay', 
        'crm-dialog',
        needsWideLayout ? 'has-logs' : 'no-logs'
      ]
    });

    return dialogRef.afterClosed();
  }

  /**
   * Opens add email dialog
   */
  openAddEmailDialog(): Observable<InputDialogResult | undefined> {
    const dialogData: InputDialogData = {
      title: 'Email cím hozzáadása',
      label: 'Email cím',
      placeholder: 'pelda@email.com',
      initialValue: '',
      required: true,
      inputType: 'email'
    };

    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: '450px',
      data: dialogData,
      disableClose: false,
      panelClass: ['input-dialog-overlay', 'crm-dialog']
    });

    return dialogRef.afterClosed();
  }
}